import { HTTP } from '@/utils';
import { REQUEST } from '@/enum';
import {
  ApiPromise,
  ReferralPayloadInterface,
  UserReferralListFilterInterface,
  ReferralCaseloadsFilterInterface,
  ReferralProcessInterface,
} from '@/types';
import { useRequestCancel } from '@/composables/useRequestCancel';

export default class ReferralService {
  static resource = 'referral';
  static requestCancel = useRequestCancel();

  static getReferralCaseload(payload?: ReferralCaseloadsFilterInterface): ApiPromise {
    let filter = '';

    if (payload) {
      filter = `?${Object.keys(payload)
        .map((item) => `${item}=${payload[item as keyof ReferralCaseloadsFilterInterface]}`)
        .join('&')}`;
    }

    return HTTP.get(`${this.resource}/caseload${filter}`, {
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_STUDENTS),
    });
  }

  static getReferralOrganizations(): ApiPromise {
    return HTTP.get(`${this.resource}/organizations`, {
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_ORGANIZATIONS),
    });
  }

  static getReferralServiceProviders(organizationIds: number[]): ApiPromise {
    return HTTP.get(`${this.resource}/service-providers`, {
      params: {
        organizations: organizationIds,
      },
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_CLINICIANS),
    });
  }

  static getReferralAssign(payload?: UserReferralListFilterInterface): ApiPromise {
    let filter = '';

    if (payload) {
      filter = `?${Object.keys(payload)
        .map((item) => `${item}=${payload[item as keyof UserReferralListFilterInterface]}`)
        .join('&')}`;
    }

    return HTTP.get(`${this.resource}/assign${filter}`, {
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_STUDENTS_ASSIGN),
    });
  }

  static getCliniciansAvailableForReferral(organizationIds: number[]): ApiPromise {
    return HTTP.get(`${this.resource}/available-to-assign`, {
      params: {
        organizations: organizationIds,
      },
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_CLINICIANS),
    });
  }

  static getReferralOptions(): ApiPromise {
    return HTTP.get(`${this.resource}/options`, {
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_OPTIONS),
    });
  }

  static process(payload: ReferralProcessInterface): ApiPromise {
    return HTTP.post(`${this.resource}/process`, {
      ...payload,
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_PROCESS),
    });
  }

  static approveReferrals(payload: ReferralPayloadInterface): ApiPromise {
    return HTTP.post(`${this.resource}/approve/${payload.id}`, {
      ...{ service_provider_id: payload.service_provider_id },
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_APPROVE),
    });
  }

  static bulkApproveReferrals(assignments: { referral_id: number; service_provider_id: number }[]): ApiPromise {
    return HTTP.post(
      `${this.resource}/approve/bulk`,
      { assignments },
      this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_BULK_APPROVE),
    );
  }

  static automatchReferrals(referralId: number): ApiPromise {
    return HTTP.post(`${this.resource}/automatch/${referralId}`, {
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_AUTOMATCH),
    });
  }

  static validateStudentImport(payload: { students: any[]; organization_id: string | null | undefined }): ApiPromise {
    return HTTP.post(`${this.resource}/student-import-validation`, {
      ...payload,
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_STUDENT_IMPORT_VALIDATION),
    });
  }

  static processStudentImport(payload: ReferralProcessInterface): ApiPromise {
    return HTTP.post(`${this.resource}/student-import-process`, {
      ...payload,
      ...this.requestCancel.requestSaverSetCancellation(REQUEST.REFERRALS_PROCESS),
    });
  }
}
